import { Route, Navigate } from 'react-router-dom';
import { TracedRoutes } from '@comeon/error-reporter';
import { useSelector } from 'react-redux';
import { CampaignRegistration } from '~/components/authentication/async.ts';
import { WeSpinRoutes as WeSpinRoutesOldFlow } from '~/components/we-spin/old-flow/index.js';
import { WeSpinRoutes as WeSpinRoutesNewFlow } from '~/components/we-spin/new-flow/index.js';
import * as Paths from '~/components/router/constants.js';
import GlobalExploreRedirect from '~/components/router/global-explore-redirect.js';
import ProductRedirect from '~/components/brand/product-redirect.js';
import ScrollToTop from '~/components/misc/scroll-to-top.js';
import Sportsbook from '~/components/sportsbook/sportsbook-async.js';
import Home from '~/components/home/home-async.js';
import {
	Bonus,
	CookiePolicy,
	ResponsibleGaming,
	About,
	UserFeedback,
	FreeBet,
	LegalBetting,
	BettingTips,
	Tax,
	Payments,
	Verification,
	ComplaintProcedure,
	Hub
} from '~/components/pages/pages-async.js';
import Shop from '~/components/shop/shop-version-control.tsx';
import Virtuals from '~/components/virtuals/virtuals-async.js';
import Promotions from '~/components/promotions/promotions-version-control.tsx';
import TVBet from '~/components/tvbet/tvbet-async.js';
import CasinoRoutes from '~/components/casino/casino-routes/casino-routes-async.js';
import LiveCasinoRoutes from '~/components/live-casino/live-casino-routes-async.js';
import SidebarRoutes from '../sidebar-paths.js';
import OverlayRoutes from '../overlay-paths.js';
import ModalRoutes from '../modal-paths.js';
import Thunderbite from '~/components/thunderbite/thunderbite-page-async';
import Splashtech from '~/components/splashtech/splashtech-page-async';
import FreeToPlay from '../../free-to-play/free-to-play-async.js';

import {
	selectIsShopEnabled,
	selectCampaignRegistration,
	selectEnableFooterBonusTab,
	selectSiteCode,
	selectIsPolishFranchise,
	selectIsNetherlandsRegulatedFranchise
} from '~/components/brand/ducks/index.js';
import {
	selectShowNewCookieBanner,
	selectHasProducts,
	selectMainContentLoading,
	selectGlobalNavigationEnabled
} from '~/components/brand/ducks/selectors.js';
import useTranslations from '~/components/translation/use-translations.ts';
import SEOContent from '~/components/misc/seo-content.js';
import SkeletonPlaceholder from '~/components/utilities/pulsating-placeholder.js';
import { MainContentLoadingStatus } from '~/components/brand/constants.js';
import { ICON_RESPONSIBLE_LINE } from '~/components/icon/index.js';
import TermsAndConditions from '~/components/pages/terms-and-conditions.js';
import PrivacyPolicy from '~/components/pages/privacy-policy-configured.js';
import InformationPageHeaderWrapper from '~/components/pages/information-page-header-wrapper.js';
import { informationPagesClasses } from '~/components/pages/constants.js';

import {
	BASE_MAIN_ROUTES_TEXT_KEYS,
	HEADER_RESPONSIBLE_RESPONSIBLE,
	MOBILE_ABOUT_US_MAINTITLE,
	MOBILE_ABOUT_US_SUBTITLE,
	MOBILE_RESPONSIBLE_GAMING_SUPPORT_TITLE
} from './routes-main-translations.js';
import { HeadWrapper, MainTitle, SubTitle } from '~/components/misc/section-head.js';
import { useTheme } from '~/components/theme/theme-context.js';
import DeferredRedirect from '../contextual-route-fallback.js';
import Affiliate from '~/components/pages/affiliate-pages-async.js';
import UNAUTHENTICATED_PATHS from './unauthenticated-paths.js';
import AppBoot from '~/components/pages/app-boot';
import { selectConfiguration } from '~/components/we-spin/old-flow/ducks/we-spin-selectors';
import { isSmallerSelector } from '~/components/layout/mediaquery.js';
import HubSupport from '~/components/support/hub/hub-support';

const SportsbookProduct = () => (
	<ProductRedirect product="sportsbook">
		<Sportsbook />
	</ProductRedirect>
);
const VirtualsProduct = () => (
	<ProductRedirect product="virtuals">
		<Virtuals />
	</ProductRedirect>
);
const TVBetProduct = () => (
	<ProductRedirect product="grytv">
		<TVBet />
	</ProductRedirect>
);

const CONTEXTUAL_PATHS = [...SidebarRoutes, ...OverlayRoutes, ...ModalRoutes];

const RoutesMain = () => {
	const isShopEnabled = useSelector(selectIsShopEnabled);
	const campaignRegistration = useSelector(selectCampaignRegistration);
	const enableFooterBonusTab = useSelector(selectEnableFooterBonusTab);
	const showNewCookieBanner = useSelector(selectShowNewCookieBanner);
	const { Logo } = useTheme();
	const siteCode = useSelector(selectSiteCode);
	const { weSpinNewFlowEnabled } = useSelector(selectConfiguration);

	const { translations: tr, isLoadingTranslations } = useTranslations(BASE_MAIN_ROUTES_TEXT_KEYS);
	const isPolishFranchise = useSelector(selectIsPolishFranchise);
	const isNetherlandsRegulatedFranchise = useSelector(selectIsNetherlandsRegulatedFranchise);

	const { hasSportsbook, hasVirtuals, hasCasino, hasWeSpin, hasTvBet, hasLivecasino } =
		useSelector(selectHasProducts);
	const mainContentLoading = useSelector(selectMainContentLoading);
	const smaller = useSelector(isSmallerSelector);
	const isGlobalNavigationEnabled = useSelector(selectGlobalNavigationEnabled);
	const showHub = smaller && isGlobalNavigationEnabled;

	return (
		!isLoadingTranslations && (
			<>
				<TracedRoutes>
					<Route path={Paths.ROUTE_HOME} element={<Home />} />
					{campaignRegistration && (
						<Route path={Paths.ROUTE_CAMPAIGN_REGISTER} element={<CampaignRegistration />} />
					)}
					{hasCasino && <Route path={`${Paths.ROUTE_CASINO}/*`} element={<CasinoRoutes />} />}
					{hasTvBet && <Route path={Paths.ROUTE_TVBET} element={<TVBetProduct />} />}
					{hasLivecasino && <Route path={`${Paths.ROUTE_LIVE_CASINO}/*`} element={<LiveCasinoRoutes />} />}
					{hasVirtuals && <Route path={`${Paths.ROUTE_VIRTUALS}/*`} element={<VirtualsProduct />} />}
					{hasSportsbook && <Route path={`${Paths.ROUTE_SPORTSBOOK}/*`} element={<SportsbookProduct />} />}
					{hasSportsbook && <Route path={`${Paths.ROUTE_LIVEBETTING}/*`} element={<SportsbookProduct />} />}
					{hasSportsbook && (
						<Route path={`${Paths.ROUTE_SPORTS}/*`} element={<Navigate to={Paths.ROUTE_SPORTSBOOK} />} />
					)}
					{enableFooterBonusTab && (
						<Route
							path={Paths.ROUTE_BONUS}
							element={
								<>
									<ScrollToTop />
									<Bonus />
								</>
							}
						/>
					)}
					<Route path={Paths.ROUTE_FREE_TO_PLAY} element={<FreeToPlay />} />
					<Route path={`${Paths.ROUTE_FREE_TO_PLAY_CUSTOM}/*`} element={<FreeToPlay />} />
					{/* {hasFreeToPlay && <Route path={`${Paths.ROUTE_FREE_TO_PLAY}/*`} element={<FreeToPlay />} />} check if we should use the star or not */}
					{isPolishFranchise && <Route path={Paths.ROUTE_FREEBET} element={<FreeBet />} />}
					{isPolishFranchise && <Route path={Paths.ROUTE_LEGAL_BETTING} element={<LegalBetting />} />}
					{isPolishFranchise && <Route path={Paths.ROUTE_BETTING_TIPS} element={<BettingTips />} />}
					{isPolishFranchise && <Route path={Paths.ROUTE_TAX} element={<Tax />} />}
					{isPolishFranchise && <Route path={Paths.ROUTE_PAYMENTS_POLAND} element={<Payments />} />}
					{isPolishFranchise && <Route path={Paths.ROUTE_VERIFICATION_POLAND} element={<Verification />} />}
					{isPolishFranchise && (
						<Route path={Paths.ROUTE_PAYMENTS} element={<Navigate to={Paths.ROUTE_PAYMENTS} />} />
					)}
					{/* TEMPORARY WESPIN FLOWS */}
					{hasWeSpin && (
						<Route
							path={`${Paths.ROUTE_WE_SPIN}/*`}
							element={weSpinNewFlowEnabled ? <WeSpinRoutesNewFlow /> : <WeSpinRoutesOldFlow />}
						/>
					)}
					<Route
						path={Paths.ROUTE_TERMSANDCONDITIONS}
						element={
							<>
								<ScrollToTop key="terms-and-conditions" />
								<TermsAndConditions />
							</>
						}
					/>
					<Route
						path={Paths.ROUTE_APP_BOOT}
						element={
							<>
								<ScrollToTop key="app-boot" />
								<AppBoot />
							</>
						}
					/>
					<Route
						path={Paths.ROUTE_PRIVACYPOLICY}
						element={
							<>
								<ScrollToTop key="privacy_policy" />
								<PrivacyPolicy />
							</>
						}
					/>
					{showNewCookieBanner && (
						<Route
							path={Paths.ROUTE_COOKIE_POLICY}
							element={
								<>
									<ScrollToTop key="cookie-policy" />
									<CookiePolicy />
								</>
							}
						/>
					)}
					<Route
						path={`${Paths.ROUTE_RESPONSIBLEGAMING}/:fromLimits?`}
						element={
							<>
								<ScrollToTop key="responsible" />
								<ResponsibleGaming
									wrapperClasses={informationPagesClasses}
									HeaderWrapper={
										<InformationPageHeaderWrapper
											iconType={ICON_RESPONSIBLE_LINE}
											mainTitle={tr[HEADER_RESPONSIBLE_RESPONSIBLE]}
											subTitle={tr[MOBILE_RESPONSIBLE_GAMING_SUPPORT_TITLE]}
										/>
									}
								/>
							</>
						}
					/>
					<Route
						path={Paths.ROUTE_ABOUT}
						element={
							<>
								<ScrollToTop key="about" />
								<About
									wrapperClasses={informationPagesClasses}
									HeaderWrapper={
										<HeadWrapper className="svg-logo--dark">
											<Logo textPageLogo={siteCode === 'getlucky'} />
											<MainTitle dataAt="section-main-title-general-global-overlay">
												{tr[MOBILE_ABOUT_US_MAINTITLE]}
											</MainTitle>
											<SubTitle dataAt="section-sub-title-header-general-global-overlay">
												{tr[MOBILE_ABOUT_US_SUBTITLE]}
											</SubTitle>
										</HeadWrapper>
									}
								/>
							</>
						}
					/>
					{tr.FEEDBACK_NAVBAR_LINK_URL && (
						<Route
							path={Paths.ROUTE_USERFEEDBACK}
							element={<UserFeedback src={tr.FEEDBACK_NAVBAR_LINK_URL} />}
						/>
					)}
					<Route
						path={Paths.ROUTE_AFFILIATE}
						element={
							<>
								<ScrollToTop key={Paths.ROUTE_AFFILIATE} />
								<Affiliate />
							</>
						}
					/>
					<Route
						path={Paths.ROUTE_SHOP}
						element={isShopEnabled ? <Shop showMarketplaceTab /> : <GlobalExploreRedirect />}
					/>
					<Route path={Paths.ROUTE_THUNDERBITE} element={<Thunderbite />} />
					<Route path={Paths.ROUTE_SPLASH_TECH} element={<Splashtech />} />
					<Route path={Paths.ROUTE_PROMOTIONS} element={<Promotions showMarketplaceTab />} />
					<Route
						path={Paths.ROUTE_PROMOTIONS_THUNDERBITE}
						element={<Promotions showMarketplaceTab thunderbiteTabRoute />}
					/>
					{isNetherlandsRegulatedFranchise && (
						<Route path={Paths.ROUTE_COMPLAINT_PROCEDURE} element={<ComplaintProcedure />} />
					)}
					{showHub && <Route path={Paths.ROUTE_HUB} element={<Hub />} />}
					{showHub && <Route path={Paths.ROUTE_SUPPORT_HUB} element={<HubSupport />} />}
					{CONTEXTUAL_PATHS.map(path => (
						<Route
							key={path}
							path={`${path}/*`}
							element={<DeferredRedirect unauthPaths={UNAUTHENTICATED_PATHS} />}
						/>
					))}
				</TracedRoutes>

				{mainContentLoading !== MainContentLoadingStatus.LOADED && (
					<SkeletonPlaceholder height="100vh" width="100%" />
				)}
				<SEOContent />
			</>
		)
	);
};

export default RoutesMain;
